import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../Components/ui/shadcn/forms/select";
import { useState } from "react";

interface UserGroupSelectProps {
  currentValue: string | undefined;
  availableOptions: string[];
  userId: string;
  onChange: (userId: string, newRoles: string) => Promise<void>;
  disabled?: boolean;
  labels?: Record<string, string>;
  colors?: Record<string, string>;
  emptyLabel?: string;
}

// Default translation map for role names
const DEFAULT_LABELS: Record<string, string> = {
  viewer: "Visualizador",
  specialist: "Especialista",
  admin: "Administrador",
  // Legacy group names
  "layer-admin": "Admin",
  users: "Público",
};

// Default color mapping
const DEFAULT_COLORS: Record<string, string> = {
  viewer: "#2196F3", // Blue
  specialist: "#9FC131", // Green
  admin: "#FF9800", // Orange
  // Legacy group colors
  "layer-admin": "#9FC131",
  users: "#0C2856",
};

// Color class mapping for Tailwind
const COLOR_CLASSES: Record<string, string> = {
  "#2196F3": "text-blue-500",
  "#9FC131": "text-lime-500",
  "#FF9800": "text-amber-500",
  "#0C2856": "text-slate-900",
  "#9E9E9E": "text-gray-400",
};

const getLabel = (name: string, labels?: Record<string, string>): string => {
  const allLabels = { ...DEFAULT_LABELS, ...labels };
  const cleanName = name.replace(/^\//, "");
  return allLabels[cleanName] || cleanName;
};

const getColorClass = (
  name?: string,
  colors?: Record<string, string>
): string => {
  const hexColor = getColor(name, colors);
  return COLOR_CLASSES[hexColor] || "text-gray-400";
};

const getColor = (name?: string, colors?: Record<string, string>): string => {
  if (!name) return "#9E9E9E";
  const allColors = { ...DEFAULT_COLORS, ...colors };
  const cleanName = name.replace(/^\//, "");
  return allColors[cleanName] || "#9E9E9E";
};

export default function UserGroupSelect({
  currentValue,
  availableOptions,
  userId,
  onChange,
  disabled = false,
  labels,
  colors,
  emptyLabel = "Sem perfil",
}: UserGroupSelectProps) {
  const [loading, setLoading] = useState(false);

  const handleChange = async (newValue: string) => {
    // Don't do anything if selecting the same value
    if (newValue === currentValue) return;

    setLoading(true);
    try {
      await onChange(userId, newValue);
    } catch (error) {
      console.error("Error changing value:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form>
      <Select
        value={currentValue || ""}
        onValueChange={handleChange}
        disabled={disabled || loading}
      >
        <SelectTrigger
          className={`w-full max-w-48 ${getColorClass(currentValue, colors)}`}
        >
          <SelectValue placeholder={emptyLabel} />
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            {availableOptions.map((option) => (
              <SelectItem
                key={option}
                value={option}
                className={`font-medium ${getColorClass(option, colors)}`}
              >
                {getLabel(option, labels)}
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
    </form>
  );
}
